import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { PasswordInput } from '../ui/password-input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Loader2, Package, Plus, Trash2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export const MovementEditModal = ({ 
  open, 
  onOpenChange, 
  movement, 
  api, 
  onSuccess 
}) => {
  const [editForm, setEditForm] = useState({
    event_date: '',
    event_time: '',
    event_name: '',
    client_name: '',
    client_contact: '',
    control_number: '',
    notes: '',
    items: []
  });
  const [adminCreds, setAdminCreds] = useState({
    admin_username: '',
    admin_password: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Reference data for editing items
  const [locations, setLocations] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Load reference data when modal opens
  const loadReferenceData = useCallback(async () => {
    if (!open) return;
    
    setLoadingData(true);
    try {
      const apiInstance = api();
      const [locRes, eqRes, catRes] = await Promise.all([
        apiInstance.get('/locations'),
        apiInstance.get('/equipment'),
        apiInstance.get('/categories')
      ]);
      setLocations(locRes.data || []);
      setEquipment(eqRes.data || []);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Error loading reference data:', error);
    } finally {
      setLoadingData(false);
    }
  }, [api, open]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    if (movement) {
      setEditForm({
        event_date: movement.event_date || '',
        event_time: movement.event_time || '',
        event_name: movement.event_name || '',
        client_name: movement.client_name || '',
        client_contact: movement.client_contact || '',
        control_number: movement.control_number || '',
        notes: movement.notes || '',
        items: (movement.items || []).map(item => ({
          equipment_id: item.equipment_id,
          equipment_name: item.equipment_name,
          location_id: item.location_id,
          location_name: item.location_name,
          quantity: item.quantity,
          serial_numbers: item.serial_numbers || '',
          condition: item.condition || 'bueno'
        }))
      });
    }
  }, [movement]);

  const handleEdit = async () => {
    if (!adminCreds.admin_username || !adminCreds.admin_password) {
      toast.error('Ingresa las credenciales de administrador');
      return;
    }

    setSaving(true);
    try {
      const apiInstance = api();
      await apiInstance.put(`/movements/${movement.id}`, {
        event_date: editForm.event_date,
        event_time: editForm.event_time,
        event_name: editForm.event_name,
        client_name: editForm.client_name,
        client_contact: editForm.client_contact,
        control_number: editForm.control_number,
        notes: editForm.notes,
        items: editForm.items,
        admin_username: adminCreds.admin_username,
        admin_password: adminCreds.admin_password
      });
      toast.success('Movimiento actualizado');
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
      onOpenChange(false);
      setAdminCreds({ admin_username: '', admin_password: '' });
    }
  };

  const handleClose = () => {
    setAdminCreds({ admin_username: '', admin_password: '' });
    onOpenChange(false);
  };
  
  const updateItem = (index, field, value) => {
    const newItems = [...editForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // If changing equipment, update the name
    if (field === 'equipment_id') {
      const eq = equipment.find(e => e.id === value);
      newItems[index].equipment_name = eq?.name || '';
    }
    // If changing location, update the name
    if (field === 'location_id') {
      const loc = locations.find(l => l.id === value);
      newItems[index].location_name = loc?.name || '';
    }
    
    setEditForm({ ...editForm, items: newItems });
  };
  
  const removeItem = (index) => {
    const newItems = editForm.items.filter((_, i) => i !== index);
    setEditForm({ ...editForm, items: newItems });
  };
  
  const addItem = () => {
    setEditForm({
      ...editForm,
      items: [...editForm.items, {
        equipment_id: '',
        equipment_name: '',
        location_id: '',
        location_name: '',
        quantity: 1,
        serial_numbers: '',
        condition: 'bueno'
      }]
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Movimiento</DialogTitle>
          <DialogDescription>
            Modifica la información del evento y los equipos. Se requiere autorización de administrador.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Fecha del Evento</Label>
              <Input
                type="date"
                value={editForm.event_date}
                onChange={(e) => setEditForm({ ...editForm, event_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Hora</Label>
              <Input
                type="time"
                value={editForm.event_time}
                onChange={(e) => setEditForm({ ...editForm, event_time: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>No. Control</Label>
              <Input
                value={editForm.control_number}
                onChange={(e) => setEditForm({ ...editForm, control_number: e.target.value })}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Nombre del Evento</Label>
            <Input
              value={editForm.event_name}
              onChange={(e) => setEditForm({ ...editForm, event_name: e.target.value })}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Input
                value={editForm.client_name}
                onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Contacto</Label>
              <Input
                value={editForm.client_contact}
                onChange={(e) => setEditForm({ ...editForm, client_contact: e.target.value })}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              rows={2}
            />
          </div>

          {/* Equipment Items */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium flex items-center gap-2">
                <Package className="h-4 w-4" />
                Equipos ({editForm.items.length})
              </Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="h-4 w-4 mr-1" />
                Agregar
              </Button>
            </div>
            
            {loadingData ? (
              <div className="text-center py-4 text-muted-foreground">
                <Loader2 className="h-5 w-5 mx-auto animate-spin" />
              </div>
            ) : editForm.items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No hay equipos registrados
              </p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {editForm.items.map((item, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-2 bg-muted/20">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs">Equipo {idx + 1}</Badge>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-destructive"
                        onClick={() => removeItem(idx)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Equipo</Label>
                        <Select
                          value={item.equipment_id}
                          onValueChange={(v) => updateItem(idx, 'equipment_id', v)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {equipment.map(eq => (
                              <SelectItem key={eq.id} value={eq.id}>{eq.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Ubicación Origen</Label>
                        <Select
                          value={item.location_id}
                          onValueChange={(v) => updateItem(idx, 'location_id', v)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map(loc => (
                              <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Cantidad</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">No. Serie</Label>
                        <Input
                          value={item.serial_numbers}
                          onChange={(e) => updateItem(idx, 'serial_numbers', e.target.value)}
                          className="h-8"
                          placeholder="Serial..."
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Condición</Label>
                        <Select
                          value={item.condition}
                          onValueChange={(v) => updateItem(idx, 'condition', v)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bueno">Bueno</SelectItem>
                            <SelectItem value="regular">Regular</SelectItem>
                            <SelectItem value="malo">Malo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Admin Credentials */}
          <div className="border-t pt-4 space-y-3">
            <Label className="text-muted-foreground">Credenciales de Administrador</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Usuario</Label>
                <Input
                  value={adminCreds.admin_username}
                  onChange={(e) => setAdminCreds({ ...adminCreds, admin_username: e.target.value })}
                  placeholder="Usuario admin"
                />
              </div>
              <div className="space-y-2">
                <Label>Contraseña</Label>
                <PasswordInput
                  value={adminCreds.admin_password}
                  onChange={(e) => setAdminCreds({ ...adminCreds, admin_password: e.target.value })}
                  placeholder="Contraseña"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleEdit} disabled={saving} className="btn-primary">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MovementEditModal;
